import { ActivityEntityName } from '../entities/shared/activity.entity.name';
import { WorkbuddyAgentToolTier } from './agent.tool.registry';

/**
 * Canonical registry of the Workbuddy Agent's PER-ENTITY read flags.
 *
 * Separate from AGENT_TOOL_REGISTRY: tool flags gate which TOOLS the agent has; these gate which
 * ENTITIES the agent may READ. The agent always keeps the entity-query tools (foundational); each
 * entity here is an independent on/off that decides whether that record type is in the agent's
 * `allowedEntities` set.
 *
 * Enforcement (jms-api): the enabled-entity set (computed from the `ai.assistant.entity.*` flags)
 * is intersected with the role-derived `allowedEntities` in `computeAIPermissions`/the request-context
 * builder, and re-checked in `AIEntityAccessGuard`. The jms-ai entity-query tools already filter by the
 * forwarded `allowedEntities`, so no jms-ai change is needed.
 *
 * DEFAULT OFF, like the tool flags: a flagged entity is readable only when its flag is ON (and the
 * role grants it). Reference lookups the agent needs to interpret records (Job Types, Priorities,
 * Stage Statuses, Companies, Tags, etc.) are intentionally NOT listed here, so they stay always-on.
 * Tasks/Appointments are not standalone readable entities yet (added in a separate effort).
 */
export interface AgentEntityRegistryEntry {
    /** The ActivityEntityName VALUE — must match the strings used in `allowedEntities`. */
    name: ActivityEntityName;
    /** Clean slug for the flag id (entity values like 'Site Pro'/'PO' would make ugly dotted ids). */
    flagKey: string;
    /** Admin-facing label shown in the grid. */
    label: string;
    /** Rollout tier (reuses the tool tiers) — drives the grid tab grouping and lock behaviour. */
    tier: WorkbuddyAgentToolTier;
}

/** Dotted feature-flag id prefix for an agent entity read flag, nested under `ai.assistant`. */
export const AGENT_ENTITY_FLAG_PREFIX = 'ai.assistant.entity.' as const;

/** Build the feature-flag id for an entity flag key (e.g. `Invoice` -> `ai.assistant.entity.Invoice`). */
export function agentEntityFlagId(flagKey: string): string {

    return `${AGENT_ENTITY_FLAG_PREFIX}${flagKey}`;
}

/** The readable business entities that can be toggled per-entity. Order = grid display order. */
export const AGENT_ENTITY_REGISTRY: ReadonlyArray<AgentEntityRegistryEntry> = [
    // Enable now
    { name: ActivityEntityName.Job, flagKey: 'Job', label: 'Jobs', tier: WorkbuddyAgentToolTier.EnableNow },
    { name: ActivityEntityName.Invoice, flagKey: 'Invoice', label: 'Invoices', tier: WorkbuddyAgentToolTier.EnableNow },
    { name: ActivityEntityName.Bill, flagKey: 'Bill', label: 'Bills', tier: WorkbuddyAgentToolTier.EnableNow },
    // Keep the established Contact flag id for rollout compatibility, but expose only the
    // page-aligned Basic Contact entity rather than the Basic+Login aggregate.
    { name: ActivityEntityName.BasicContact, flagKey: 'Contact', label: 'Contacts', tier: WorkbuddyAgentToolTier.EnableNow },
    { name: ActivityEntityName.Customer, flagKey: 'Customer', label: 'Customers', tier: WorkbuddyAgentToolTier.EnableNow },
    { name: ActivityEntityName.Contractor, flagKey: 'Contractor', label: 'Contractors', tier: WorkbuddyAgentToolTier.EnableNow },
    { name: ActivityEntityName.Supplier, flagKey: 'Supplier', label: 'Suppliers', tier: WorkbuddyAgentToolTier.EnableNow },
    { name: ActivityEntityName.WorkBuddyCompany, flagKey: 'WorkBuddyCompany', label: 'Companies', tier: WorkbuddyAgentToolTier.EnableNow },
    { name: ActivityEntityName.ContactEmployee, flagKey: 'ContactEmployee', label: 'Employees', tier: WorkbuddyAgentToolTier.EnableNow },
    { name: ActivityEntityName.LoginContact, flagKey: 'LoginContact', label: 'External Users', tier: WorkbuddyAgentToolTier.EnableNow },
    { name: ActivityEntityName.Catalogue, flagKey: 'Catalogue', label: 'Catalogue', tier: WorkbuddyAgentToolTier.EnableNow },

    // WIP & Experimental
    { name: ActivityEntityName.SitePro, flagKey: 'SitePro', label: 'Sites', tier: WorkbuddyAgentToolTier.Wip },
    // DO NOT move Equipment or Quotes to EnableNow until their Agent role domains are
    // visible again. AccessEquipment / AccessQuotes still exist on the module, but
    // AI_PERMISSION_DOMAINS in security.module.details.component.ts omits those rows,
    // so admins cannot grant them from Settings > Roles. They are also omitted from
    // getAIEntityCatalog so listAvailableEntities cannot advertise them. These flags
    // stay DEFAULT OFF and are the current ship gate; turning a flag on without a
    // grantable role right and a catalogue entry leaves the entity unreachable
    // (except leftover stored grants). Restore the Equipment and Quotes domain rows
    // and catalogue entries before promoting these out of Wip.
    { name: ActivityEntityName.Equipment, flagKey: 'Equipment', label: 'Equipment', tier: WorkbuddyAgentToolTier.Wip },
    { name: ActivityEntityName.QuotePro, flagKey: 'QuotePro', label: 'Quotes', tier: WorkbuddyAgentToolTier.Wip },
    { name: ActivityEntityName.PurchaseOrder, flagKey: 'PurchaseOrder', label: 'Purchase Orders', tier: WorkbuddyAgentToolTier.Wip },
];

/** Set of every flaggable entity NAME (value), for "is this entity gated?" checks during filtering. */
export const AGENT_ENTITY_REGISTRY_NAMES: ReadonlySet<string> = new Set(AGENT_ENTITY_REGISTRY.map(entry => entry.name));

/** True if the entity is one that the per-entity flags gate (vs an always-on reference lookup). */
export function isFlaggableAgentEntity(entityName: string): boolean {

    return AGENT_ENTITY_REGISTRY_NAMES.has(entityName);
}
